<template>
  <PrepeersImage
    v-if="showLogoImage"
    :src="logoSrc"
    :alt="props.alt"
    :preset="logoPreset"
    :image-class="imageClass"
    fallback-src=""
    loading="lazy"
    @error="onLogoImageError" />
  <span
    v-else
    :class="initialsClass"
    aria-hidden="true"
    >{{ logoInitial }}</span
  >
</template>

<script lang="ts" setup>
import type { PropType, ComputedRef, Ref } from 'vue'

import { computed, ref, watch } from 'vue'
import PrepeersImage from '~/components/core/PrepeersImage.vue'
import type { PrepeersEntityLogoProps, PrepeersEntityLogoVariant } from '~/types/PrepeersEntityLogo'
import type { PrepeersImagePreset } from '~/types/PrepeersImage'
import { getEntityLogoInitial, resolveEntityLogoSrc } from '~/utils/entityLogo'

/**
 * Définit les props du composant PrepeersEntityLogo.
 */
const props: PrepeersEntityLogoProps = defineProps({
  logoPict: {
    type: String,
    default: '',
  },
  entityName: {
    type: String,
    required: true,
  },
  variant: {
    type: String as PropType<PrepeersEntityLogoVariant>,
    default: 'hero',
  },
  alt: {
    type: String,
    default: '',
  },
})

const imageLoadFailed: Ref<boolean> = ref<boolean>(false)

const logoPreset: ComputedRef<PrepeersImagePreset> = computed(
  (): PrepeersImagePreset => (props.variant === 'card' ? 'entityLogoCard' : 'entityLogoHero'),
)

const logoSrc: ComputedRef<string> = computed(() => resolveEntityLogoSrc(props.logoPict))

const showLogoImage: ComputedRef<boolean> = computed(
  (): boolean => logoSrc.value.length > 0 && !imageLoadFailed.value,
)

watch(
  (): string => props.logoPict ?? '',
  (): void => {
    imageLoadFailed.value = false
  },
)

/**
 * Affiche l'initiale si le logo distant ne charge pas.
 * @returns {void}
 */
function onLogoImageError(): void {
  imageLoadFailed.value = true
}

const logoInitial: ComputedRef<string> = computed(() =>
  getEntityLogoInitial(props.logoPict, props.entityName),
)

const imageClass: ComputedRef<string> = computed(() =>
  props.variant === 'card' ? 'entity-logo__img entity-logo__img--card' : 'hero-banner__logo-img',
)

const initialsClass: ComputedRef<string> = computed(() =>
  props.variant === 'card'
    ? 'entity-logo__initials entity-logo__initials--card'
    : 'hero-banner__logo-initials',
)
</script>
